// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Testimonial {
  id: string;
  name: string;
  role: 'parent' | 'athlete' | 'coach';
  title?: string;
  content: string;
  rating?: number;
  featured: boolean;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

interface TestimonialFormData {
  name: string;
  role: 'parent' | 'athlete' | 'coach';
  title?: string;
  content: string;
  rating?: number | null;
  featured: boolean;
  is_published: boolean;
}

export const useTestimonials = (onlyPublished: boolean = false) => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTestimonials = async () => {
    try {
      setIsLoading(true);
      let query = supabase
        .from('testimonials')
        .select('*')
        .order('created_at', { ascending: false });

      if (onlyPublished) {
        query = query.eq('is_published', true);
      }

      const { data, error } = await query;

      if (error) throw error;

      setTestimonials(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching testimonials:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const addTestimonial = async (testimonialData: TestimonialFormData) => {
    try {
      const { data, error } = await supabase
        .from('testimonials')
        .insert([testimonialData])
        .select()
        .single();

      if (error) {
        console.error('Supabase error adding testimonial:', error);
        throw new Error(`Failed to add testimonial: ${error.message}`);
      }

      if (!data) {
        throw new Error('No data returned after adding testimonial');
      }

      setTestimonials(prev => [data, ...prev]);
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add testimonial';
      setError(errorMessage);
      console.error('Error in addTestimonial:', err);
      throw new Error(errorMessage);
    }
  };

  const updateTestimonial = async (id: string, testimonialData: Partial<TestimonialFormData>) => {
    try {
      const { data, error } = await supabase
        .from('testimonials')
        .update(testimonialData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setTestimonials(prev =>
        prev.map(testimonial =>
          testimonial.id === id ? data : testimonial
        )
      );
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update testimonial');
      throw err;
    }
  };

  const deleteTestimonial = async (id: string) => {
    try {
      const { error } = await supabase
        .from('testimonials')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setTestimonials(prev => prev.filter(testimonial => testimonial.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete testimonial');
      throw err;
    }
  };

  const getFeaturedTestimonials = () => {
    return testimonials.filter(t => t.featured && t.is_published);
  };

  const getTestimonialsByRole = (role: 'parent' | 'athlete' | 'coach') => {
    return testimonials.filter(t => t.role === role && t.is_published);
  };

  useEffect(() => {
    fetchTestimonials();
  }, [onlyPublished]);

  return {
    testimonials,
    isLoading,
    error,
    addTestimonial,
    updateTestimonial,
    deleteTestimonial,
    getFeaturedTestimonials,
    getTestimonialsByRole,
    refetch: fetchTestimonials
  };
};